import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormHelperText,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import Swal from "sweetalert2";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useState, useEffect } from "react";
import { NumericFormat } from "react-number-format";
import Header from "../../../components/Header";
import {
  graphqlQuery,
  generateAndExecuteMutation,
} from "../../../data/Axios/DynamicService";
import { AccountsandFinanceurl } from "../../../config";
import {
  fixedAssetsCategories,
  allAssetTypes,
  allDepreciationMethods,
  allDepreciationSchedules,
} from "../../../data/Columns/Queries/FixedAssetManagement/fixedAssetsmanagement";

const base_url = AccountsandFinanceurl.uri;

const FixedAssetForm = ({ isEditing = false, data = {}, onClose, refetchData }) => {
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken") || "{}");
  const userId = parseInt(decodedToken.Id) || null;

  // Initial state
  const [initialValues, setInitialValues] = useState({
    categoryId: "",
    typeId: "",
    methodId: "",
    scheduleId: "",
    assetCode: "",
    assetName: "",
    description: "",
    serialNumber: "",
    purchaseDate: new Date().toISOString().split("T")[0],
    purchaseCost: "",
    currentValue: "",
    location: "",
    department: "",
    custodian: "",
    status: "Active",
    isActive: true,
  });

  // Dropdown data
  const [categories, setCategories] = useState([]);
  const [assetTypes, setAssetTypes] = useState([]);
  const [methods, setDepreciationMethods] = useState([]);
  const [schedules, setSchedules] = useState([]);

  // Fetch master data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catData, typeData, methodData, schedData] = await Promise.all([
          graphqlQuery(fixedAssetsCategories, base_url),
          graphqlQuery(allAssetTypes, base_url),
          graphqlQuery(allDepreciationMethods, base_url),
          graphqlQuery(allDepreciationSchedules, base_url),
        ]);
        setCategories(catData || []);
        setAssetTypes(typeData || []);
        setDepreciationMethods(methodData || []);
        setSchedules(schedData || []);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  // Populate values when editing
  useEffect(() => {
    if (isEditing && data) {
      setInitialValues({
        categoryId: data.categoryId || "",
        typeId: data.typeId || "",
        methodId: data.methodId || "",
        scheduleId: data.scheduleId || "",
        assetCode: data.assetCode || "",
        assetName: data.assetName || "",
        description: data.description || "",
        serialNumber: data.serialNumber || "",
        purchaseDate: data.purchaseDate ? data.purchaseDate.split("T")[0] : "",
        purchaseCost: data.purchaseCost || "",
        currentValue: data.currentValue || "",
        location: data.location || "",
        department: data.department || "",
        custodian: data.custodian || "",
        status: data.status || "Active",
        isActive: data.isActive ?? true,
      });
    }
  }, [isEditing, data]);

  // Validation
  const validationSchema = yup.object().shape({
    categoryId: yup.string().required("Category is required"),
    typeId: yup.string().required("Asset type is required"),
    methodId: yup.string().required("Depreciation method is required"),
    assetCode: yup.string().required("Asset code is required"),
    assetName: yup.string().required("Asset name is required"),
    purchaseDate: yup.date().required("Purchase date is required"),
    purchaseCost: yup.number().min(0, "Must be positive").required("Purchase cost is required"),
    currentValue: yup.number().min(0, "Must be positive").required("Current value is required"),
    location: yup.string().required("Location is required"),
    department: yup.string().required("Department is required"),
    custodian: yup.string().required("Custodian is required"),
  });

  // Handle submit
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      let resolvedScheduleId = values.scheduleId ? parseInt(values.scheduleId) : null;
      if (!resolvedScheduleId && values.categoryId && values.typeId) {
        const matched = schedules.find(
          (s) =>
            s.categoryId === parseInt(values.categoryId) &&
            s.typeId === parseInt(values.typeId) &&
            (!values.methodId || s.methodId === parseInt(values.methodId))
        );
        if (matched) {
          resolvedScheduleId = matched.id;
        }
      }

      if (!resolvedScheduleId) {
        Swal.fire({
          icon: "warning",
          title: "Depreciation Schedule Required",
          text: "No active Depreciation Schedule found for the selected Category, Asset Type, and Method. Please create a Depreciation Schedule under Fixed Assets Setups first before adding an asset for this type.",
        });
        return;
      }

      const baseMutationData = {
        categoryId: parseInt(values.categoryId),
        typeId: parseInt(values.typeId),
        methodId: parseInt(values.methodId),
        scheduleId: resolvedScheduleId,
        assetCode: values.assetCode,
        assetName: values.assetName,
        description: values.description || "",
        serialNumber: values.serialNumber || "",
        purchaseDate: values.purchaseDate,
        purchaseCost: parseFloat(values.purchaseCost),
        currentValue: parseFloat(values.currentValue),
        location: values.location,
        department: values.department,
        custodian: values.custodian,
        status: values.status || "Active",
        isActive: values.isActive ?? true,
      };

      const mutationData = isEditing
        ? { ...baseMutationData, id: data.id, updatedBy: userId }
        : { ...baseMutationData, createdBy: userId };

      const responseKey = isEditing ? "updatedAsset" : "newAsset";
      const mutationName = isEditing ? "updateFixedAsset" : "createFixedAsset";

      const response = await generateAndExecuteMutation(
        mutationName,
        mutationData,
        responseKey,
        base_url
      );

      if (response) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `Fixed Asset ${isEditing ? "updated" : "created"} successfully.`,
        }).then(() => {
          if (refetchData) refetchData();
          if (onClose) onClose();
        });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: error?.message || "Something went wrong while saving the asset.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // JSX Render
  return (
    <Box m="20px">
      <Header
        title={isEditing ? "Edit Fixed Asset" : "Create Fixed Asset"}
        subtitle={isEditing ? "Update asset details" : "Add a new fixed asset"}
      />

      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        enableReinitialize
        validationSchema={validationSchema}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
          isSubmitting,
        }) => {
          const filteredAssetTypes = values.categoryId
            ? assetTypes.filter((t) => t.categoryId === parseInt(values.categoryId))
            : assetTypes;

          return (
            <form onSubmit={handleSubmit}>
              <Box
                display="grid"
                gap="25px"
                gridTemplateColumns="repeat(2, minmax(0, 1fr))"
                sx={{
                  "& > div": { gridColumn: isNonMobile ? undefined : "span 2" },
                }}
              >
                {/* Category */}
                <FormControl fullWidth variant="filled" error={touched.categoryId && !!errors.categoryId}>
                  <InputLabel>Category *</InputLabel>
                  <Select
                    name="categoryId"
                    value={values.categoryId}
                    onChange={(e) => {
                      handleChange(e);
                      setFieldValue("typeId", "");
                    }}
                  >
                    {categories.map((cat) => (
                      <MenuItem key={cat.id} value={cat.id}>
                        {cat.categoryName}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.categoryId && errors.categoryId && (
                    <FormHelperText>{errors.categoryId}</FormHelperText>
                  )}
                </FormControl>

                {/* Asset Type */}
                <FormControl fullWidth variant="filled" error={touched.typeId && !!errors.typeId}>
                  <InputLabel>Asset Type *</InputLabel>
                  <Select
                    name="typeId"
                    value={values.typeId}
                    onChange={handleChange}
                    disabled={!values.categoryId && filteredAssetTypes.length === 0}
                  >
                    {filteredAssetTypes.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.typeName}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.typeId && errors.typeId && (
                    <FormHelperText>{errors.typeId}</FormHelperText>
                  )}
                </FormControl>

                {/* Depreciation Method */}
                <FormControl fullWidth variant="filled" error={touched.methodId && !!errors.methodId}>
                  <InputLabel>Depreciation Method *</InputLabel>
                  <Select name="methodId" value={values.methodId} onChange={handleChange}>
                    {methods.map((method) => (
                      <MenuItem key={method.id} value={method.id}>
                        {method.methodName}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.methodId && errors.methodId && (
                    <FormHelperText>{errors.methodId}</FormHelperText>
                  )}
                </FormControl>

                {/* Asset Code */}
                <TextField
                  fullWidth
                  variant="filled"
                  label="Asset Code / Tag *"
                  name="assetCode"
                  value={values.assetCode}
                  onChange={handleChange}
                  error={touched.assetCode && !!errors.assetCode}
                  helperText={touched.assetCode && errors.assetCode}
                />

                {/* Serial Number */}
                <TextField
                  fullWidth
                  variant="filled"
                  label="Serial Number"
                  name="serialNumber"
                  value={values.serialNumber}
                  onChange={handleChange}
                  error={touched.serialNumber && !!errors.serialNumber}
                  helperText={touched.serialNumber && errors.serialNumber}
                />

                {/* Asset Name */}
                <TextField
                  fullWidth
                  variant="filled"
                  label="Asset Name *"
                  name="assetName"
                  value={values.assetName}
                  onChange={handleChange}
                  error={touched.assetName && !!errors.assetName}
                  helperText={touched.assetName && errors.assetName}
                />

                {/* Description */}
                <TextField
                  fullWidth
                  variant="filled"
                  label="Description"
                  name="description"
                  value={values.description}
                  onChange={handleChange}
                />

                {/* Purchase Date */}
                <TextField
                  fullWidth
                  type="date"
                  variant="filled"
                  label="Purchase Date *"
                  name="purchaseDate"
                  InputLabelProps={{ shrink: true }}
                  value={values.purchaseDate}
                  onChange={handleChange}
                  error={touched.purchaseDate && !!errors.purchaseDate}
                  helperText={touched.purchaseDate && errors.purchaseDate}
                />

                {/* Purchase Cost */}
                <NumericFormat
                  customInput={TextField}
                  fullWidth
                  variant="filled"
                  label="Purchase Cost *"
                  name="purchaseCost"
                  value={values.purchaseCost}
                  thousandSeparator=","
                  allowNegative={false}
                  onValueChange={(valuesObj) => {
                    setFieldValue("purchaseCost", valuesObj.value);
                  }}
                  onBlur={handleBlur}
                  error={touched.purchaseCost && !!errors.purchaseCost}
                  helperText={touched.purchaseCost && errors.purchaseCost}
                />

                {/* Current Value */}
                <NumericFormat
                  customInput={TextField}
                  fullWidth
                  variant="filled"
                  label="Current Value *"
                  name="currentValue"
                  value={values.currentValue}
                  thousandSeparator=","
                  allowNegative={false}
                  onValueChange={(valuesObj) => {
                    setFieldValue("currentValue", valuesObj.value);
                  }}
                  onBlur={handleBlur}
                  error={touched.currentValue && !!errors.currentValue}
                  helperText={touched.currentValue && errors.currentValue}
                />

                {/* Location */}
                <TextField
                  fullWidth
                  variant="filled"
                  label="Location *"
                  name="location"
                  value={values.location}
                  onChange={handleChange}
                  error={touched.location && !!errors.location}
                  helperText={touched.location && errors.location}
                />

                {/* Department */}
                <TextField
                  fullWidth
                  variant="filled"
                  label="Department *"
                  name="department"
                  value={values.department}
                  onChange={handleChange}
                  error={touched.department && !!errors.department}
                  helperText={touched.department && errors.department}
                />

                {/* Custodian */}
                <TextField
                  fullWidth
                  variant="filled"
                  label="Custodian *"
                  name="custodian"
                  value={values.custodian}
                  onChange={handleChange}
                  error={touched.custodian && !!errors.custodian}
                  helperText={touched.custodian && errors.custodian}
                />
              </Box>

              {/* Submit Button */}
              <Box display="flex" justifyContent="flex-end" mt="20px">
                <Button
                  type="submit"
                  color="secondary"
                  variant="contained"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Submitting..." : isEditing ? "Update" : "Create"}
                </Button>
              </Box>
            </form>
          );
        }}
      </Formik>
    </Box>
  );
};

export default FixedAssetForm;

